// @ts-nocheck
import debounce from 'lodash/debounce';
import { Dayjs } from 'dayjs';
import { EntityId } from 'charting_library';
import { dayjs, ET } from 'util/shared';
import { hiroV2HistoryLimit } from 'util/hiro';
import { HiroChartDatafeedIntfc } from 'types/hiro';

let cnt = 0;
export interface Artifacts {
  enabled: boolean;
  entityIdsFromDayStartMs: Record<number, EntityId[]>;
}

export abstract class IndicatorManager<ArtifactKey extends string> {
  datafeed: HiroChartDatafeedIntfc;

  artifactsFromKeyFromSym: Record<string, Record<ArtifactKey, Artifacts>>;

  abstract fetchAssets(dates: Dayjs[]): Promise<void>;
  abstract createArtifacts(dates: Dayjs[]): Promise<void>;

  // hiro idicators have odd lifecycles.  They get created and destroyed frequently
  // but we don't want to destroy the artifacts unless we really REALLY want them
  // destroyed.
  debouncedArtifactDestructor: () => void;

  debouncedSetVisibleRange: (begMs: number, endMs: number) => void;

  // need to serialize ensureArtifacts as there are data structure race conditions
  // within the creation of studies and between this debouncing and awaiting
  // lastEnsureArtifacts, ensureArtifacts _should_ be effectively serialized
  debouncedEnsureArtifacts: () => void;

  lastEnsureArtifacts: Promise<void> = Promise.resolve();

  visibleRangeBegMs?: number;
  visibleRangeEndMs?: number;

  ensureArtifactsLog: typeof console.log;
  removeArtifactsLog: typeof console.log;

  constructor(datafeed: HiroChartDatafeedIntfc) {
    this.cnt = ++cnt;
    this.datafeed = datafeed;
    this.artifactsFromKeyFromSym = {};
    this.debouncedArtifactDestructor = debounce(
      this.removeArtifacts.bind(this),
      25,
    );
    this.debouncedSetVisibleRange = debounce(
      this.setVisibleRange.bind(this),
      300,
    );
    this.debouncedEnsureArtifacts = debounce(() => {
      this.lastEnsureArtifacts = this.ensureArtifacts();
    }, 75);
    this.lastEnsureArtifacts = Promise.resolve();
    this.ensureArtifactsLog = debounce(console.log, 1_000, { leading: true });
    this.removeArtifactsLog = debounce(console.log, 1_000, { leading: true });
  }

  setVisibleRange(begMs: number, endMs: number) {
    this.visibleRangeBegMs = begMs;
    this.visibleRangeEndMs = endMs;
    this.removeArtifacts(true);
    this.debouncedEnsureArtifacts();
  }

  setEnable(sym: string, enable: boolean, key?: ArtifactKey) {
    const artifactsFromKey = (this.artifactsFromKeyFromSym[sym] =
      this.artifactsFromKeyFromSym[sym] ?? {});

    const keys =
      key != null ? [key] : (Object.keys(artifactsFromKey) as ArtifactKey[]);
    for (const key of keys) {
      const artifacts = (artifactsFromKey[key] = artifactsFromKey[key] ?? {
        enabled: false,
        entityIdsFromDayStartMs: {},
      });
      if (!artifacts.enabled && enable) {
        artifacts.enabled = enable;
        this.debouncedEnsureArtifacts();
      } else if (artifacts.enabled && !enable) {
        this.debouncedArtifactDestructor();
        artifacts.enabled = false;
      }
    }
  }

  removeArtifacts(removeAll?: boolean) {
    try {
      const chart = this.datafeed.chartingLibraryWidget?.activeChart();
      if (chart == null) {
        return;
      }
      for (const artifactsFromKey of Object.values(
        this.artifactsFromKeyFromSym,
      )) {
        const artifactsList: Artifacts[] = Object.values(artifactsFromKey);
        for (const artifacts of artifactsList) {
          if (artifacts.enabled && !(removeAll ?? false)) {
            continue;
          }
          for (const entityIds of Object.values(
            artifacts.entityIdsFromDayStartMs,
          )) {
            for (const entityId of entityIds) {
              chart.removeEntity(entityId);
            }
            entityIds.length = 0;
          }
        }
      }
    } catch (e) {
      this.removeArtifactsLog(
        'Issue removing artifacts.  Probably due to destruction race condition. ',
        e,
      );
    }
  }

  async ensureArtifacts() {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/strict-boolean-expressions
    if (this.datafeed?.tradingViewWidget?.removed ?? false) {
      return;
    }
    try {
      await this.lastEnsureArtifacts;
    } catch (_e) {
      // if lastEnsureArtifacts fails for any reason, carry on and try again,
      // rather than blocking forever
    }

    try {
      let dates: Dayjs[] = [];
      for (
        let d = dayjs(this.visibleRangeBegMs).tz(ET).startOf('day');
        d < dayjs(this.visibleRangeEndMs);
        d = d.add(1, 'day')
      ) {
        dates.push(d);
      }
      const historyLimit = hiroV2HistoryLimit();
      dates = dates.filter((d) => d >= historyLimit);

      await this.fetchAssets(dates);

      await this.createArtifacts(dates);
    } catch (e) {
      this.ensureArtifactsLog('Failure ensuring artifacts', e);
    }
  }
}
